import {
  AllForemanId,
  CacheKeys,
  UnassignedForemanId,
  useConfig,
  usePlanning,
  useSessionStorage,
} from '@bechtel/client-common';
import { useSideSheetState } from '@bechtel/gds';
import { CommodityMilestone } from '@bechtel/types';
import { ChangeEvent, FC, MutableRefObject, useCallback, useEffect, useRef, useState } from 'react';
import { BackLogContainerStyled } from '../../styled';
import { MilestoneIdentifier, SelectionSection } from '../../types';
import { BacklogSearchDrawer } from './backlog-drawer';
import { BacklogList } from './backlog-list';
import { BacklogHeader } from './header';
import { BacklogFilter, defaultValues } from './types';

interface BacklogProps {
  setSelectedMilestoneDraggableIds: (value: string[] | []) => void;
  selectedMilestoneDraggableIds: string[];
  draggingMilestoneId: string;
  unplanMilestones: () => void;
  milestoneIdentifiers: MutableRefObject<Map<string, MilestoneIdentifier>>;
  backlogMilestoneData: MutableRefObject<CommodityMilestone[]>;
  backlogRefetch: MutableRefObject<(() => void) | null>;
  activeSelectionSection: SelectionSection | null;
  setActiveSelectionSection: (section: SelectionSection | null) => void;
  isCollapsed: boolean;
  onToggleCollapse: () => void;
}

export const Backlog: FC<BacklogProps> = ({
  setSelectedMilestoneDraggableIds,
  selectedMilestoneDraggableIds,
  draggingMilestoneId,
  unplanMilestones,
  milestoneIdentifiers,
  backlogMilestoneData,
  backlogRefetch,
  activeSelectionSection,
  setActiveSelectionSection,
  isCollapsed,
  onToggleCollapse,
}) => {
  const { numPlanningNextWeeks } = useConfig();
  const { isReplan, selectedWpList, selectedForeman } = usePlanning();
  const { visible, open, close } = useSideSheetState(false);
  const [searchVal, setSearchVal] = useState<string>();
  const persistedDrawerValues = useRef(defaultValues);
  const [formData, setFormData] = useSessionStorage<BacklogFilter>(
    CacheKeys.BacklogFilter,
    persistedDrawerValues.current || defaultValues
  );

  const onBacklogFilterSubmit = useCallback(
    (values: BacklogFilter) => {
      setFormData(() => {
        persistedDrawerValues.current = values;
        return values;
      });
    },
    [setFormData]
  );

  const onSearch = (e: ChangeEvent<HTMLInputElement>) => {
    setSearchVal(e.target.value);
  };

  useEffect(() => {
    if (isReplan) {
      onBacklogFilterSubmit({ ...defaultValues, expiredMilestoneFilter: true });
    }
  }, [isReplan]);

  return (
    <>
      {visible && (
        <BacklogSearchDrawer close={close} onBacklogFilterSubmit={onBacklogFilterSubmit} initialValues={formData} />
      )}
      <BackLogContainerStyled
        isDrawerOpen={visible}
        style={
          isCollapsed
            ? { width: '40px', minWidth: '40px' }
            : numPlanningNextWeeks == 3
              ? { width: '21%' }
              : { width: '25%' }
        }
      >
        <BacklogHeader
          open={open}
          onChange={onSearch}
          filterValues={formData}
          selectedMilestoneDraggableIds={selectedMilestoneDraggableIds}
          unplanMilestones={unplanMilestones}
          isCollapsed={isCollapsed}
          onToggleCollapse={onToggleCollapse}
        />
        {!isCollapsed &&
          selectedWpList &&
          selectedWpList.length > 0 &&
          selectedForeman &&
          selectedForeman?.id !== AllForemanId &&
          selectedForeman?.id !== UnassignedForemanId && (
            <BacklogList
              setSelectedMilestoneDraggableIds={setSelectedMilestoneDraggableIds}
              selectedMilestoneDraggableIds={selectedMilestoneDraggableIds}
              draggingMilestoneId={draggingMilestoneId}
              searchVal={searchVal}
              formData={formData}
              milestoneIdentifiers={milestoneIdentifiers}
              backlogMilestoneData={backlogMilestoneData}
              backlogRefetch={backlogRefetch}
              activeSelectionSection={activeSelectionSection}
              setActiveSelectionSection={setActiveSelectionSection}
            />
          )}
      </BackLogContainerStyled>
    </>
  );
};
